import React from 'react'
import { useTranslation } from 'react-i18next'

import { BuildOutlined, InfoOutlined, PaletteOutlined } from '@mui/icons-material'
import { Box, IconButton, Skeleton, Stack } from '@mui/material'
import { alpha } from '@mui/material/styles'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { shadeColor } from '../../../../helpers/shadeColor'
import { UserLanguage } from '../../../account/types/User'
import { LiveEventInfoTooltipContent } from '../../../live-events/components/LiveEventsCalendar/LiveEventsCalendarItem/LiveEventsCalendarTrackingEventItem/LiveEventInfoTooltipContent'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../../live-events/hooks/useLiveEventTagGroups'
import { LiveEventsCalendarTimelineType, TrackingEventTimelineItem } from '../../../live-events/types/LiveEvents'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { TrackingEvent } from '../../../live-events/types/TrackingEvents'
import { getTrackingEventDescriptionSnippet } from '../../../live-events/utils/trackingEventDescription'
import { BlueprintEventKpi } from '../../hooks/useBlueprintEventKpis'
import { BlueprintEventRow } from '../helpers/blueprintEventRows'

// Icon size of the rail; the buttons are exactly this big so the 10px gap is icon-to-icon.
export const EVENT_ROW_ICON_SIZE = 16
export const EVENT_ROW_ICON_GAP = '10px'

// Hook for the owning row's hover rule: the grey links darken.
export const EVENT_LINK_CLASS = 'BlueprintEventRowActions__link'

// Value type of the event-type → tag-group map, forwarded into the event's
// calendar hover popup (`LiveEventInfoTooltipContent`).
export type EventTypeTagGroup = ReturnType<typeof useLiveEventTagGroupsMapByEventTypeId>[string]

export interface BlueprintEventRowActionsProps {
  row: BlueprintEventRow
  trackingEvent?: TrackingEvent
  tagGroup?: EventTypeTagGroup
  popupBgColor: string
  trackedGame?: TrackedGame
  userLanguage: UserLanguage
  // KPI chips of the popup. Without it the popup shows none: the event getter carries no performance data.
  kpi?: BlueprintEventKpi
}

const iconButtonSx = { p: 0, width: EVENT_ROW_ICON_SIZE, height: EVENT_ROW_ICON_SIZE, flexShrink: 0 } as const
const iconSx = { fontSize: EVENT_ROW_ICON_SIZE } as const

// Percent the tag-group colour is darkened by for a NEW event, same as the calendar item.
const NEW_EVENT_SHADE_PERCENT = -50

/**
 * Always-visible rail at the end of one event row in the adopters tree table:
 * a mechanic-change marker (blue) and a reskin marker (green when the event
 * re-dresses an earlier one), each an empty slot otherwise so the icons stay
 * aligned down the rows, and the calendar-style details
 * popup. The tracker deep link lives on the event name itself. The owning
 * row's hover rule darkens the link via `EVENT_LINK_CLASS`.
 */
const BlueprintEventRowActionsComponent: React.FC<BlueprintEventRowActionsProps> = ({
  row,
  trackingEvent,
  tagGroup,
  popupBgColor,
  trackedGame,
  userLanguage,
  kpi,
}) => {
  const { t } = useTranslation()
  const { event } = row
  // NEW (an event type's first appearance) is the same rule as the calendar and the New Events strip.
  // The "Event version first appearance" line is never shown here: the calendar popups hardcode firstInstance={false},
  // and the popup follows the calendar (Ossi's decision). Only the NEW line ("Event type first appearance") remains.
  const isNewEvent = Boolean(trackingEvent?.highlighted && trackingEvent.firstInstance)
  const tooltipBgColor = isNewEvent && tagGroup?.colorHex ? shadeColor(tagGroup.colorHex, NEW_EVENT_SHADE_PERCENT) : popupBgColor
  const mechanicChangeLabel = t('live-events:live_event_evolution_type_mechanic_change')
  const reskinLabel = row.previousEventName
    ? t('market-analytics:event_table_reskin_of', { name: row.previousEventName })
    : t('market-analytics:event_table_reskin')

  return (
    <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={EVENT_ROW_ICON_GAP} sx={{ minHeight: EVENT_ROW_ICON_SIZE }}>
      {row.isMechanicChange ? (
        <GRTooltip content={mechanicChangeLabel} styles={{ display: 'inline-flex', flexShrink: 0 }}>
          <Box
            component="span"
            role="img"
            aria-label={mechanicChangeLabel}
            data-testid="event-mechanic-change-marker"
            sx={{ ...iconButtonSx, display: 'inline-flex', color: 'info.main' }}
          >
            <BuildOutlined sx={iconSx} />
          </Box>
        </GRTooltip>
      ) : (
        <Box component="span" aria-hidden sx={iconButtonSx} />
      )}
      {row.isReskin ? (
        <GRTooltip content={reskinLabel} styles={{ display: 'inline-flex', flexShrink: 0 }}>
          <Box
            component="span"
            role="img"
            aria-label={reskinLabel}
            data-testid="event-reskin-marker"
            sx={{ ...iconButtonSx, display: 'inline-flex', color: 'success.main' }}
          >
            <PaletteOutlined sx={iconSx} />
          </Box>
        </GRTooltip>
      ) : (
        <Box component="span" aria-hidden sx={iconButtonSx} />
      )}
      {/* Same hover popup as the Live Events calendar. Its event payload is fetched
          by the row itself (event getter) when the row mounts, so it is ready before
          the first hover. */}
      <GRTooltip
        content={
          trackingEvent ? (
            <LiveEventInfoTooltipContent
              item={
                {
                  trackingEventId: trackingEvent.eventId,
                  trackedGame,
                  trackingEvent,
                  type: LiveEventsCalendarTimelineType.TrackingEvent,
                  highlighted: false,
                  new: isNewEvent,
                  tagGroup,
                  performanceChanges: kpi?.performanceChanges,
                  performanceEnded: kpi ? true : undefined,
                  start_time: kpi?.startTime,
                  end_time: kpi?.endTime,
                } as TrackingEventTimelineItem
              }
              itemContext={{ title: trackingEvent.name || event.name || '' }}
              descriptionRawHtml={getTrackingEventDescriptionSnippet(trackingEvent, userLanguage)}
              bgColor={tooltipBgColor}
              newItem={isNewEvent}
              mechanicChange={trackingEvent.mechanicChange && row.isMechanicChange}
              firstInstance={false}
              inactive={trackingEvent.active === false}
            />
          ) : (
            <Skeleton variant="rounded" width={280} height={120} />
          )
        }
        popoverBorderColor={tooltipBgColor}
        disableMaxWidth
        styles={{ display: 'inline-flex', flexShrink: 0 }}
      >
        <IconButton
          size="small"
          className={EVENT_LINK_CLASS}
          aria-label={t('market-analytics:event_table_details')}
          sx={(theme) => ({ ...iconButtonSx, color: alpha(theme.palette.text.primary, 0.3) })}
        >
          <InfoOutlined sx={iconSx} />
        </IconButton>
      </GRTooltip>
    </Stack>
  )
}

export const BlueprintEventRowActions = React.memo(BlueprintEventRowActionsComponent)
BlueprintEventRowActions.displayName = 'BlueprintEventRowActions'
